import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import { checkExistingCollections, createFundPayments } from 'lib/api/funds';
import CustomButton from 'lib/designSystem/Button';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import HeaderWithActions from 'lib/designSystem/HeaderWithActions';
import EnhancedTable from 'lib/designSystem/Table';
import { showAlert } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { AuthState } from '../../../../../../../services/redux/authSlice';
import EditArrearDialog from '../../../../Payments/components/PayIns/components/Conciliation/Conciliate/EditArrearDialog';
import CollectionsWarningDialog from './components/CollectionsWarningDialog';

type EditArrearDocument = {
  documentId: string;
  arrearStructure: {
    arrearType: string;
    arrearRate: number;
    arrearSegments: {
      min: number;
      max: number;
      value: number;
    }[];
  };
  hasConciliations: boolean;
} | null;

const MultiPaymentFundResell = (props: any) => {
  const {
    open,
    setOpen,
    payments,
    setPayments,
    setSelected,
    callBackCreatePendingAccounts,
    setUp,
    pagination,
  } = props;
  const dispatch = useDispatch();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);
  const [loading, setLoading] = useState(false);
  const [openConfirmMultiPayment, setOpenConfirmMultiPayment] = useState(false);
  const newOperationDate = dayjs().hour(12).minute(0).second(0).millisecond(0);
  const [paymentDate, setPaymentDate] = useState<Dayjs>(newOperationDate);
  const [editArrearDocument, setEditArrearDocument] =
    useState<EditArrearDocument>(null);
  const [openEditArrearDialog, setOpenEditArrearDialog] = useState(false);
  const [openCollectionsWarning, setOpenCollectionsWarning] = useState(false);
  const [documentsWithWarnings, setDocumentsWithWarnings] = useState<any[]>([]);
  const [validationLoading, setValidationLoading] = useState(false);

  const validParams = () => {
    if (!paymentDate) {
      alert('Debes seleccionar una fecha');
      return false;
    }
    const allPaymentsHaveAmount = payments.every(
      (payment: { amount: number }) => payment.amount,
    );
    if (!allPaymentsHaveAmount) {
      alert('Debes completar el monto a registrar');
      return false;
    }
    return true;
  };

  const handleCheckCollections = async () => {
    if (!validParams()) {
      return;
    }

    setValidationLoading(true);
    setOpenConfirmMultiPayment(false);

    try {
      const paymentsToCheck = payments.map((payment: any) => ({
        factoringResellId: payment.factoringResellId,
        amount: payment.amount,
        documentId: payment.documentId,
      }));

      const result = await checkExistingCollections(dispatch, {
        payments: paymentsToCheck,
      });

      setValidationLoading(false);

      if (result.hasWarnings) {
        const docsWithWarnings = result.documentsWithCollections.filter(
          (doc: any) => doc.hasExistingCollections || doc.wouldCreateExcess,
        );
        setDocumentsWithWarnings(docsWithWarnings);
        setOpenCollectionsWarning(true);
      } else {
        await handleSubmit();
      }
    } catch (error) {
      setValidationLoading(false);
      showAlert(dispatch, 'error', 'Error al validar los pagos');
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setOpenCollectionsWarning(false);
    try {
      const paymentsWithDate = payments.map(
        (payment: { amount: number; documentAmount: number }) => ({
          ...payment,
          date: paymentDate,
        }),
      );
      const { date, resellsCompletedIds, surplusWarnings } =
        await createFundPayments(dispatch, {
          payments: paymentsWithDate,
        });
      setOpen(false);
      setSelected([]);
      if (
        surplusWarnings &&
        Array.isArray(surplusWarnings) &&
        surplusWarnings.length > 0
      ) {
        showAlert(
          dispatch,
          'warning',
          'Pago registrado correctamente. Hubo un error al crear el excedente de la venta.',
        );
      } else {
        showAlert(dispatch, 'success', 'Pagos registrados correctamente');
      }
      await callBackCreatePendingAccounts(resellsCompletedIds, date);
    } catch (error: any) {
      showAlert(
        dispatch,
        'error',
        error.response?.data?.message || 'Error al registrar los pagos',
      );
    }
    setLoading(false);
  };

  const handleChangeAmountToConciliate = (event: any) => {
    const amount = event.target.value.split('.').join('').split('$').join('');
    const payment = payments.find(
      (payment: any) => String(payment.id) === event.target.name,
    );

    payment.amount = Number(amount);
    setPayments([...payments]);
  };

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      const dateWithFixedHour = date
        .hour(12)
        .minute(0)
        .second(0)
        .millisecond(0);
      setPaymentDate(dateWithFixedHour);
    } else {
      setPaymentDate(dayjs().hour(12).minute(0).second(0).millisecond(0));
    }
  };

  const DateSelector = () => {
    return (
      <div>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es-mx">
          <DatePicker
            label="Fecha"
            value={paymentDate}
            onChange={handleDateChange}
            format="DD/MM/YYYY"
            slotProps={{
              textField: {
                fullWidth: true,
                required: true,
              },
            }}
          />
        </LocalizationProvider>
      </div>
    );
  };

  const handleEditArrearStructure = (paymentId: string) => {
    const payment = payments.find((payment: any) => payment.id === paymentId);
    if (!payment) return showAlert(dispatch, 'error', 'Pago no encontrado');
    const disabled =
      payment.conciliationLog.length > 0
        ? payment.conciliationLog[payment.conciliationLog.length - 1].date >=
          paymentDate.toDate()
        : false;
    if (disabled)
      return showAlert(
        dispatch,
        'error',
        'No se puede editar la mora de este documento porque tiene conciliaciones futuras',
      );
    setEditArrearDocument({
      documentId: payment.documentId,
      arrearStructure: payment.arrearStructure,
      hasConciliations: payment.conciliationLog.length > 0,
    });
    setOpenEditArrearDialog(true);
  };

  const handleSaveArrearStructure = () => {
    setEditArrearDocument(null);
    setOpenEditArrearDialog(false);
    setPayments([]);
    setSelected([]);
    void setUp({ page: pagination.page, pageSize: pagination.pageSize });
    setOpen(false);
  };

  return (
    <>
      <Dialog maxWidth="lg" fullWidth open={open}>
        <div style={{ padding: 20, minHeight: 200 }}>
          {loading || validationLoading ? (
            <CenteredCircularProgress type="dialog" />
          ) : (
            <div>
              <HeaderWithActions
                title="Registrar pagos"
                item={DateSelector()}
              />
              <Typography variant="body1"></Typography>
              <div style={{ padding: '10px 0px' }}>
                <EnhancedTable
                  data={payments}
                  dataHeaders={[
                    {
                      key: 'factoringResellId',
                      label: 'Id',
                      type: 'numeric-id',
                    },
                    {
                      key: 'folio',
                      label: 'Folio',
                    },
                    {
                      key: 'businessName',
                      label: 'Cliente',
                    },
                    {
                      key: 'stakeholderName',
                      label: 'Deudor',
                    },
                    {
                      key: 'fundName',
                      label: 'Fondo',
                    },
                    {
                      key: 'documentAmount',
                      label: 'Monto Documento',
                      type: 'money',
                    },
                    {
                      key: 'documentArrearStructure',
                      label: 'Tasa de mora documento cliente',
                      type: 'arrear-structure-editable',
                      customAction: handleEditArrearStructure,
                      extraData: {
                        operationDate: paymentDate.toDate(),
                        user,
                      },
                    },
                    {
                      key: 'amount',
                      label: 'Monto a registrar',
                      type: 'money',
                      handleChange: handleChangeAmountToConciliate,
                    },
                  ]}
                />
              </div>
              <DialogActions>
                <CustomButton onClick={() => setOpen(false)} color="secondary">
                  Salir
                </CustomButton>
                <CustomButton
                  onClick={() => setOpenConfirmMultiPayment(true)}
                  color="primary"
                >
                  Marcar pagos
                </CustomButton>
              </DialogActions>
            </div>
          )}
        </div>
        <Dialog
          open={openConfirmMultiPayment}
          onClose={() => setOpenConfirmMultiPayment(false)}
        >
          <DialogTitle>
            <Typography variant="h6">Confirmar pagos</Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1">
              Estás seguro de querer registrar los avisos de pago desde los
              fondos?
            </Typography>
          </DialogContent>
          <DialogActions>
            <CustomButton
              onClick={() => setOpenConfirmMultiPayment(false)}
              color="secondary"
            >
              Cancelar
            </CustomButton>
            <CustomButton
              onClick={() => handleCheckCollections()}
              color="primary"
            >
              Confirmar
            </CustomButton>
          </DialogActions>
        </Dialog>
      </Dialog>
      {openCollectionsWarning && (
        <CollectionsWarningDialog
          open={openCollectionsWarning}
          onClose={() => setOpenCollectionsWarning(false)}
          onConfirm={() => void handleSubmit()}
          documentsWithWarnings={documentsWithWarnings}
        />
      )}
      {openEditArrearDialog && (
        <EditArrearDialog
          open={openEditArrearDialog}
          setOpen={setOpenEditArrearDialog}
          document={editArrearDocument}
          onSave={handleSaveArrearStructure}
          conciliationDate={new Date(paymentDate.toDate())}
        />
      )}
    </>
  );
};

export default MultiPaymentFundResell;
